/**
 * Minimal Web Audio API engine for UI sound effects.
 * Adapted from soundcn (github.com/kapishdima/soundcn) — zero dependencies.
 */

let audioContext: AudioContext | null = null
const bufferCache = new Map<string, AudioBuffer>()

function getContext(): AudioContext {
  if (!audioContext) {
    audioContext = new AudioContext()
  }
  if (audioContext.state === 'suspended') {
    audioContext.resume()
  }
  return audioContext
}

async function decodeDataUri(dataUri: string): Promise<AudioBuffer> {
  const cached = bufferCache.get(dataUri)
  if (cached) return cached

  const base64 = dataUri.split(',')[1] ?? ''
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }

  const ctx = getContext()
  const buffer = await ctx.decodeAudioData(bytes.buffer)
  bufferCache.set(dataUri, buffer)
  return buffer
}

export interface PlaySoundOptions {
  volume?: number
  playbackRate?: number
}

export async function playSound(
  dataUri: string,
  options: PlaySoundOptions = {}
): Promise<void> {
  try {
    const ctx = getContext()
    const buffer = await decodeDataUri(dataUri)

    const source = ctx.createBufferSource()
    source.buffer = buffer
    source.playbackRate.value = options.playbackRate ?? 1

    const gain = ctx.createGain()
    gain.gain.value = options.volume ?? 1

    source.connect(gain)
    gain.connect(ctx.destination)
    source.start(0)
  } catch {
    // Silently fail — audio is non-critical UX enhancement
  }
}
